import type { Lang } from "./api";

/**
 * Texts of the Residents section (docs/22 §12). Residents of the Azores are served in Portuguese and English (as on
 * WhatsApp); the other languages of the area read the English.
 */
const en = {
  nav: "Residents",
  title: "Residents",
  lead: "Your family, the documents for the mobility subsidy and your flights at the resident price.",
  notResident: "This section is for Azores residents who travel with us on WhatsApp.",
  notResidentHow: "Write to us on WhatsApp to join: we take care of the subsidy for you.",
  loading: "Loading…",
  notices: "News",
  account: "Your account",
  statusVerified: "Verified",
  statusPending: "Being checked by our team",
  statusOther: "Not finished",
  missing: "Still needed",
  mandate: "Mandate",
  mandateSigned: "Signed on {date} (version {v})",
  mandateMissing: "Not signed yet: without it we cannot advance the subsidy.",
  mandateSign: "Sign the mandate",
  openAdvance: "Subsidy we advanced, not yet paid by the State",
  family: "Family",
  familyLead: "Everyone who flies on your account. Each one needs their Cartão de Cidadão; students their enrolment proof.",
  addPerson: "Add someone",
  edit: "Edit",
  remove: "Remove",
  removeConfirm: "Remove {name} from your family? Trips already booked stay as they are.",
  holder: "Account holder",
  relation_child: "Child",
  relation_spouse: "Spouse or partner",
  relation_parent: "Parent",
  relation_grandparent: "Grandparent",
  relation_other: "Other",
  relation_self: "Account holder",
  memberVerified: "Checked",
  memberPending: "To check",
  memberRejected: "Needs attention",
  givenNames: "First names",
  surname: "Surnames",
  birthDate: "Date of birth",
  nif: "NIF",
  relation: "Relationship",
  docNumber: "Cartão de Cidadão number",
  docExpiry: "Valid until",
  save: "Save",
  saving: "Saving…",
  cancel: "Cancel",
  documents: "Documents",
  noDocuments: "No documents yet.",
  upload: "Send a document",
  uploading: "Sending…",
  replace: "Replace",
  view: "View",
  kind: "What is it?",
  kind_citizen_card_front: "Cartão de Cidadão (front)",
  kind_citizen_card_back: "Cartão de Cidadão (back)",
  kind_passport: "Passport",
  kind_address_proof: "Proof of address",
  kind_residence_certificate: "Residence certificate (Finanças)",
  kind_enrolment_proof: "Enrolment proof (students)",
  kind_employment_proof: "Employment proof",
  kind_household_proof: "Household proof",
  kind_other: "Other",
  doc_received: "Received",
  doc_verified: "Checked",
  doc_rejected: "Not accepted",
  doc_expired: "Expired",
  expires: "valid until {date}",
  householdDocs: "Account documents",
  trips: "Your flights",
  noTrips: "No flights yet. Book on WhatsApp: you pay only your share.",
  youPaid: "You paid",
  subsidy: "Subsidy",
  advanced: "advanced by us",
  claims: "Subsidy claims",
  claim_prepared: "Ready to file",
  claim_submitted: "Filed with the State",
  claim_granted: "Approved",
  claim_paid: "Paid",
  claim_refused: "Refused",
  claim_cancelled: "Withdrawn",
  claim_returned: "Returned",
  toPay: "To pay",
  pay: "Pay",
  change: "Change the date",
  journey: "Which flight",
  outbound: "Outbound",
  return: "Return",
  newDate: "New date",
  searchOptions: "See options",
  noOptions: "No flights that day.",
  penalty: "airline penalty {amount}",
  youPay: "You pay {amount}",
  choose: "Choose",
  confirmChange: "Change to {flights} on {date}? You pay {amount}.",
  changed: "Done: your new e-ticket is in the trip.",
  cancelTrip: "Cancel the trip",
  cancelConfirm: "{explanation}\n\nCancel the trip?",
  cancelled: "The trip is cancelled.",
  trip_held: "Held",
  trip_awaiting_payment: "Waiting for payment",
  trip_awaiting_approval: "Being confirmed",
  trip_paid: "Paid",
  trip_ticketing_review: "Tickets being issued",
  trip_ticketing: "Tickets being issued",
  trip_ticketed: "Confirmed",
  trip_cancelled: "Cancelled",
  trip_failed: "Needs our team",
  trip_expired: "Expired",
  whatsappSignIn: "Send the code on WhatsApp",
  whatsappNumber: "WhatsApp number",
  codeSentWhatsapp: "We sent a code on WhatsApp to {to}.",
  tooLarge: "The file is larger than 10 MB.",
  error: "Something went wrong. Please try again.",
};

type Key = keyof typeof en;

const pt: Record<Key, string> = {
  nav: "Residentes",
  title: "Residentes",
  lead: "A sua família, os documentos para o subsídio de mobilidade e os seus voos ao preço de residente.",
  notResident: "Esta secção é para residentes dos Açores que viajam connosco pelo WhatsApp.",
  notResidentHow: "Escreva-nos no WhatsApp para aderir: tratamos do subsídio por si.",
  loading: "A carregar…",
  notices: "Novidades",
  account: "A sua conta",
  statusVerified: "Verificada",
  statusPending: "Em verificação pela nossa equipa",
  statusOther: "Por concluir",
  missing: "Ainda falta",
  mandate: "Mandato",
  mandateSigned: "Assinado a {date} (versão {v})",
  mandateMissing: "Ainda não assinado: sem ele não podemos adiantar o subsídio.",
  mandateSign: "Assinar o mandato",
  openAdvance: "Subsídio adiantado por nós, ainda por pagar pelo Estado",
  family: "Família",
  familyLead: "Todas as pessoas que viajam na sua conta. Cada uma precisa do Cartão de Cidadão; os estudantes também do comprovativo de matrícula.",
  addPerson: "Acrescentar alguém",
  edit: "Alterar",
  remove: "Retirar",
  removeConfirm: "Retirar {name} da sua família? As viagens já marcadas ficam como estão.",
  holder: "Titular da conta",
  relation_child: "Filho ou filha",
  relation_spouse: "Cônjuge ou companheiro",
  relation_parent: "Pai ou mãe",
  relation_grandparent: "Avô ou avó",
  relation_other: "Outro",
  relation_self: "Titular da conta",
  memberVerified: "Verificado",
  memberPending: "Por verificar",
  memberRejected: "Precisa de atenção",
  givenNames: "Nomes próprios",
  surname: "Apelidos",
  birthDate: "Data de nascimento",
  nif: "NIF",
  relation: "Parentesco",
  docNumber: "Número do Cartão de Cidadão",
  docExpiry: "Válido até",
  save: "Guardar",
  saving: "A guardar…",
  cancel: "Cancelar",
  documents: "Documentos",
  noDocuments: "Ainda sem documentos.",
  upload: "Enviar um documento",
  uploading: "A enviar…",
  replace: "Substituir",
  view: "Ver",
  kind: "O que é?",
  kind_citizen_card_front: "Cartão de Cidadão (frente)",
  kind_citizen_card_back: "Cartão de Cidadão (verso)",
  kind_passport: "Passaporte",
  kind_address_proof: "Comprovativo de morada",
  kind_residence_certificate: "Certidão de domicílio fiscal (Finanças)",
  kind_enrolment_proof: "Comprovativo de matrícula (estudantes)",
  kind_employment_proof: "Comprovativo de trabalho",
  kind_household_proof: "Comprovativo do agregado",
  kind_other: "Outro",
  doc_received: "Recebido",
  doc_verified: "Verificado",
  doc_rejected: "Não aceite",
  doc_expired: "Expirado",
  expires: "válido até {date}",
  householdDocs: "Documentos da conta",
  trips: "Os seus voos",
  noTrips: "Ainda sem voos. Marque pelo WhatsApp: paga só a sua parte.",
  youPaid: "Pagou",
  subsidy: "Subsídio",
  advanced: "adiantado por nós",
  claims: "Pedidos de subsídio",
  claim_prepared: "Pronto a submeter",
  claim_submitted: "Submetido ao Estado",
  claim_granted: "Deferido",
  claim_paid: "Pago",
  claim_refused: "Indeferido",
  claim_cancelled: "Retirado",
  claim_returned: "Devolvido",
  toPay: "Por pagar",
  pay: "Pagar",
  change: "Mudar a data",
  journey: "Que voo",
  outbound: "Ida",
  return: "Volta",
  newDate: "Nova data",
  searchOptions: "Ver opções",
  noOptions: "Sem voos nesse dia.",
  penalty: "penalização da companhia {amount}",
  youPay: "Paga {amount}",
  choose: "Escolher",
  confirmChange: "Mudar para {flights} a {date}? Paga {amount}.",
  changed: "Feito: o novo bilhete eletrónico está na viagem.",
  cancelTrip: "Cancelar a viagem",
  cancelConfirm: "{explanation}\n\nCancelar a viagem?",
  cancelled: "A viagem foi cancelada.",
  trip_held: "Reservada",
  trip_awaiting_payment: "À espera do pagamento",
  trip_awaiting_approval: "Em confirmação",
  trip_paid: "Paga",
  trip_ticketing_review: "Bilhetes em emissão",
  trip_ticketing: "Bilhetes em emissão",
  trip_ticketed: "Confirmada",
  trip_cancelled: "Cancelada",
  trip_failed: "Com a nossa equipa",
  trip_expired: "Expirada",
  whatsappSignIn: "Enviar o código pelo WhatsApp",
  whatsappNumber: "Número de WhatsApp",
  codeSentWhatsapp: "Enviámos um código pelo WhatsApp para {to}.",
  tooLarge: "O ficheiro tem mais de 10 MB.",
  error: "Algo correu mal. Tente outra vez.",
};

export type ResidentKey = Key;

export function rt(lang: Lang, key: Key, vars: Record<string, string | number> = {}): string {
  const text = (lang === "pt" ? pt : en)[key] ?? en[key] ?? key;
  return text.replace(/\{(\w+)\}/g, (_m, k: string) => String(vars[k] ?? ""));
}

export const hasResidentKey = (key: string): key is Key => key in en;
